import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

const ANGULARJS_ROUTES = [
  // * entry
  'mobileAppSecure', // /secure/:projectId
  // * partnerAccount - tabs
  'base.account', // account/:tabId
  // * partner-admin - tabs
  'base.company', // company
  'base.offices', // offices
  'base.offices.add', // /add
  'base.offices.edit', // /edit/id/:office_id
  'base.users', // users
  'base.users.add', // /add
  'base.users.edit', // /edit/id/:user_id
  'base.bankAccounts', // bankAccounts
  'base.bankAccounts.add', // /add
  'base.bankAccounts.edit', // /edit/:bankAccountDetails
  // * partnerEligibility
  'base.eligibility', // eligibility
  'base.eligibility.application', // :projectId
  // * partnerLogin
  'base.login', // login
  'base.resetRequest', // reset-request
  'base.newPassword', // new-password?reset_password_token?password_verification_code
  // * partnerProjects - tabs
  'base.projects', // projects?taskId?filterPayload
  'base.pipeline', // pipeline?filterPayload
  'base.projects.details', // /id/:projectId
  'base.projects.details.messages', // /messages
  'base.projects.details.documents', // /documents
  'base.projects.details.improvements', // /improvements
  'base.projects.details.photos', // /photos
  // * partnerResources - tabs
  'base.resources',
  'base.resources.articles', // {articleType:collateral|news|training|present|web-assets}
  'base.resources.articles.details', // /id/{articleId:int}
  // * partnerTasks - tabs
  'base.tasks', // tasks?task?filterPayload
  // * ygreneProposalTool
  'base.proposal', // proposal
  'base.proposal-project', // proposal/{projectId}
];

interface MobileAppWindow {
  biometric: {
    available: boolean;
    display: 'Face ID' | 'Touch ID' | 'Fingerprint' | 'Face';
    enabled: boolean;
    face: boolean;
    touch: boolean;
  };
  featureFlags: {
    changeEnvironment: boolean;
    projectPhotoUpload: boolean;
    secureUpload: boolean;
    getCurrentLocation: boolean;
  };
  version: string;
}
declare const window: {
  nsWebViewInterface: { emit: (type: string, payload: any) => void };
  angular: any;
  androidApp?: MobileAppWindow;
  iosApp?: MobileAppWindow;
};

@Injectable({
  providedIn: 'root',
})
export class AngularjsMigrationService {
  webView = window.nsWebViewInterface;

  // getters
  get androidApp() {
    return window.androidApp;
  }

  get iosApp() {
    return window.iosApp;
  }

  get mobileApp() {
    return window.androidApp || window.iosApp;
  }

  emitToNativeApp(eventName: string, payload: unknown): void {
    const bridge = window.nsWebViewInterface;
    if (bridge?.emit) {
      bridge.emit(eventName, payload);
      return;
    }

    const hostWindow = window as unknown as Window;
    if (hostWindow.parent && hostWindow.parent !== hostWindow) {
      hostWindow.parent.postMessage(
        {
          type: 'NATIVE_WEBVIEW_EMIT',
          eventName,
          payload,
        },
        '*'
      );
    }
  }

  // subjects
  mobileApp$ = new BehaviorSubject<MobileAppWindow | undefined>(undefined);
  biometricLogin$ = new Subject<{
    email: string;
    password: string;
    biometric: boolean;
  }>();
  changeEnvironment$ = new Subject<void>();
  photoUpload$ = new Subject<{ base64: string }>();
  secureUpload$ = new Subject<{ url: string; payload: any }>();
  secureUploadError$ = new Subject<unknown>();
  secureUploadComplete$ = new Subject<void>();
  documentsPhotoCaptured$ = new Subject<{ base64: string; contentType?: string }>();
  documentsPhotoCaptureError$ = new Subject<unknown>();

  private loginScope = {
    $ctrl: {
      email: '',
      password: '',
      login: () => {
        this.biometricLogin$.next({
          email: this.loginScope.$ctrl.email,
          password: this.loginScope.$ctrl.password,
          biometric: this.mobileApp?.biometric.enabled || false,
        });
      },
    },
  };

  constructor(private zone: NgZone) {
    const hostWindow = window as unknown as Window;
    hostWindow.addEventListener('message', (event: MessageEvent) => {
      if (event.source !== hostWindow.parent || event.source === hostWindow) {
        return;
      }
      if (event.data?.type === 'SECURE_UPLOAD_RESULT' && event.data.url) {
        this.zone.run(() => {
          this.secureUpload$.next({ url: event.data.url, payload: event.data.payload });
        });
        return;
      }
      if (event.data?.type === 'SECURE_UPLOAD_ERROR') {
        this.zone.run(() => {
          this.secureUploadError$.next(undefined);
        });
        return;
      }
      if (event.data?.startApplication === true) {
        try {
          sessionStorage.setItem('apply_start_application', '1');
        } catch {
          // sessionStorage can be blocked in some webviews
        }
      }
      const androidApp = event.data?.androidApp as MobileAppWindow | undefined;
      const iosApp = event.data?.iosApp as MobileAppWindow | undefined;
      if (!androidApp && !iosApp) {
        return;
      }
      if (androidApp) {
        window.androidApp = androidApp;
      }
      if (iosApp) {
        window.iosApp = iosApp;
      }
      this.zone.run(() => {
        this.mobileApp$.next(this.mobileApp);
      });
    });

    window.angular = {
      element: (arg: string) => {
        if (arg === '.sign-in-button') {
          return {
            scope: () => {
              return this.loginScope;
            },
          };
        }

        if (arg === '#upload-project-photos') {
          return {
            scope: () => {
              return {
                $ctrl: {
                  onMobileAppSelect: (base64: string) => {
                    this.photoUpload$.next({ base64 });
                  },
                },
                $digest: () => {
                  return;
                },
              };
            },
          };
        }

        return {
          injector: () => {
            return {
              get: (arg: string) => {
                return {
                  _injected: () => {
                    this.zone.run(() => {
                      this.mobileApp$.next(this.mobileApp);
                    });
                    return;
                  },
                  secureUpload: (url: string, payload: any) => {
                    this.secureUpload$.next({ url, payload });
                  },
                  secureUploadError: (error?: unknown) => {
                    this.zone.run(() => {
                      this.secureUploadError$.next(error);
                    });
                  },
                  secureUploadComplete: () => {
                    this.zone.run(() => {
                      this.secureUploadComplete$.next();
                    });
                  },
                  documentsPhotoCaptured: (payload?: { base64: string; contentType?: string }) => {
                    this.zone.run(() => {
                      this.documentsPhotoCaptured$.next(payload || { base64: '' });
                    });
                  },
                  documentsPhotoCaptureError: (error?: unknown) => {
                    this.zone.run(() => {
                      this.documentsPhotoCaptureError$.next(error);
                    });
                  },
                };
              },
            };
          },
        };
      },
    };

    // iOS sets window.iosApp after WebView bootstrap. The gear icon reads
    // mobileApp$, which is only updated if native also calls _injected().
    this.publishMobileAppWhenAvailable();
  }

  private publishMobileAppWhenAvailable() {
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const app = this.mobileApp;
      if (app) {
        this.zone.run(() => this.mobileApp$.next(app));
        clearInterval(timer);
      } else if (Date.now() - startedAt > 3000) {
        clearInterval(timer);
      }
    }, 200);
  }
}
